import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { api, unwrap } from '../api/client.js';
import { errorMessage } from '../errors.js';
import { formatAgo } from '../i18n.js';
import { Button, LinkButton, Notice } from './ui.js';

export interface SiteAccess {
  readonly block: 'bot_protection' | 'auth_required' | null;
  readonly checkerIp: string | null;
  readonly userAgent: string;
}

/** What becomes visible once our check is let in (site-dashboard.html: bot-protection). */
const UNLOCKS = ['grade', 'availability', 'links', 'google', 'speed', 'legal'] as const;

function CopyValue({ value }: { value: string }) {
  const { t } = useTranslation();
  const [copied, setCopied] = useState(false);
  return (
    <>
      <code>{value}</code>{' '}
      <Button
        variant="ghost"
        size="sm"
        onClick={() => {
          navigator.clipboard
            ?.writeText(value)
            .then(() => {
              setCopied(true);
              setTimeout(() => setCopied(false), 1500);
            })
            .catch(() => undefined);
        }}
      >
        {copied ? t('setup.copied') : t('setup.copy')}
      </Button>
    </>
  );
}

/**
 * The dashboard when a bot protection or a password hides the site (T-34): not "offline", but what
 * the customer can do, step by step, and what becomes visible afterwards.
 */
export function SetupPanel({
  siteId,
  access,
  lastCheckAt,
  sslDaysLeft = null,
  crawlPages,
}: {
  siteId: string;
  access: SiteAccess;
  lastCheckAt: string | null;
  /** What we can already see from outside: the certificate's remaining days. */
  sslDaysLeft?: number | null;
  /** How many pages the link check reads once it may (plan, 25 before the ownership is confirmed). */
  crawlPages: number | null;
}) {
  const { t, i18n } = useTranslation();
  const client = useQueryClient();
  const recheck = useMutation({
    mutationFn: () => unwrap(api.POST('/sites/{siteId}/recheck', { params: { path: { siteId } } })),
    onSuccess: () => {
      // The check runs within seconds; the dashboard refreshes itself every 30 s, sooner here.
      setTimeout(() => void client.invalidateQueries({ queryKey: ['site', siteId] }), 15_000);
    },
  });
  if (!access.block) return null;

  if (access.block === 'auth_required')
    return (
      <Notice
        kind="warning"
        title={t('setup.auth.title')}
        action={
          <LinkButton to={`/sites/${siteId}/settings#basic-auth`} variant="primary" size="sm">
            {t('setup.auth.addPassword')}
          </LinkButton>
        }
      >
        {t('setup.auth.text')}
      </Notice>
    );

  return (
    <section className="setup" aria-labelledby="setup-title">
      <div>
        <h2 id="setup-title">{t('setup.bot.title')}</h2>
        <p className="bodyM">{t('setup.bot.text')}</p>
      </div>
      <ul className="chips" aria-label={t('setup.statusLabel')}>
        <li>
          <span className="badge ok">{t('setup.status.server')}</span>
        </li>
        {sslDaysLeft !== null ? (
          <li>
            <span className="badge ok">{t('setup.status.ssl', { count: sslDaysLeft })}</span>
          </li>
        ) : null}
        <li>
          <span className="badge warn">{t('setup.status.content')}</span>
        </li>
      </ul>
      <ol>
        <li>
          <b>{t('setup.bot.step1')}:</b> {t('setup.bot.step1Text')}
        </li>
        {access.checkerIp ? (
          <li>
            <b>{t('setup.bot.step2')}:</b> {t('setup.bot.step2Text')}{' '}
            <CopyValue value={access.checkerIp} />
          </li>
        ) : null}
        <li>
          <b>{t('setup.bot.step3')}</b> {t('setup.bot.step3Text')}{' '}
          <CopyValue value={access.userAgent} />
        </li>
        <li>
          <b>{t('setup.bot.step4')}:</b>{' '}
          <Button
            size="sm"
            busy={recheck.isPending}
            disabled={recheck.isSuccess}
            onClick={() => recheck.mutate()}
          >
            {t('setup.recheck')}
          </Button>{' '}
          {lastCheckAt ? (
            <span className="last">
              {t('setup.lastAttempt', { time: formatAgo(lastCheckAt, i18n.language) })}
            </span>
          ) : null}
          {recheck.isSuccess ? <p role="status">{t('setup.recheckQueued')}</p> : null}
          {recheck.isError ? <p role="alert">{errorMessage(recheck.error, t)}</p> : null}
        </li>
      </ol>
      <div>
        <b className="h4s">{t('setup.unlockTitle')}</b>
        <ul>
          {/* The pages of the link check only once the plan is known: no number made up. */}
          {UNLOCKS.filter((key) => key !== 'links' || crawlPages !== null).map((key) => (
            <li key={key}>
              {t(`setup.unlock.${key}`, key === 'links' ? { count: crawlPages } : {})}
            </li>
          ))}
        </ul>
      </div>
    </section>
  );
}
